import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useRealmAuthContext } from '@interxfi/api-client';
import type { WhoAmIContext } from '@interxfi/api-client/realm-auth/store';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Avatar } from '@interxfi/design-system/components/avatar';
import { DropdownMenu } from '@interxfi/design-system/components/dropdown-menu/dropdown-menu';
import type { DropdownMenuItem } from '@interxfi/design-system/components/dropdown-menu/list-item/dropdown-menu-list-item';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import { Typography } from '@interxfi/design-system/components/typography';
import LogoutIcon from '@interxfi/design-system/icons/react/icon-logout';
import SettingsIcon from '@interxfi/design-system/icons/react/icon-settings';
import styles from './user-menu.module.scss';

const ACTIONS_GROUP = 'UserActions';

function buildUserMenuItems(closeMobileNav: VoidFunction, context: WhoAmIContext | undefined, isAdmin: boolean): DropdownMenuItem[] {
  return [
    {
      id: 'summary',
      label: (
        <VerticalStack gap={2}>
          <Typography lineHeight="1rem" size="0.875rem">
            {[context?.data?.profile?.firstName, context?.data?.profile?.lastName].filter(Boolean).join(' ') || <Skeleton charCount={12} />}
          </Typography>

          <Typography lineHeight="1rem" size="0.75rem">
            {context?.email || <Skeleton charCount={24} />}
          </Typography>

          {isAdmin ? <Pill variant="neutral">Admin</Pill> : null}
        </VerticalStack>
      ),
    },
    {
      id: 'settings',
      group: ACTIONS_GROUP,
      icon: <SettingsIcon aria-hidden width={16} />,
      LinkElement: Link,
      label: 'Settings',
      to: '/settings',
      onClick: closeMobileNav,
    },
    {
      id: 'signOut',
      group: ACTIONS_GROUP,
      icon: <LogoutIcon aria-hidden width={16} />,
      LinkElement: Link,
      label: 'Sign Out',
      to: '/logout',
      onClick: closeMobileNav,
    },
  ] as const satisfies DropdownMenuItem[];
}

interface UserMenuProps {
  closeMobileNav: VoidFunction;
}

export function UserMenu({ closeMobileNav }: UserMenuProps) {
  const userContext = useRealmAuthContext();
  const currentAccess = useActiveRealmAPIContext();
  const menuItems = useMemo(
    () => buildUserMenuItems(closeMobileNav, userContext, Boolean(currentAccess.activeAccess?.data?.spec?.isAdmin)),
    [closeMobileNav, userContext, currentAccess.activeAccess?.data?.spec?.isAdmin],
  );
  const renderedName = [userContext?.data?.profile?.firstName, userContext?.data?.profile?.lastName].filter(Boolean).join(' ');

  return (
    <DropdownMenu
      activator={
        <button className={styles.userMenuActivator} type="button">
          <Avatar aria-label="User Menu" name={renderedName} size="medium" variant="primary" />
        </button>
      }
      align="end"
      groupStyle="border"
      items={menuItems}
      label="User Menu"
      labelIsHidden
      matchActivatorWidth={false}
      popoverClassName={styles.userMenuPopover}
      side="bottom"
    />
  );
}
